import { classLevels, classLevelWords, isDuet, type ClassLevel, type ClassRules, type Group, type TeamMember, type TimetableGroup } from "@teamna/shared";
import { useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { flushSync } from "react-dom";
import {
  Avatar,
  AvatarName,
  Badge,
  Button,
  Checkbox,
  ChipRow,
  ClassLevelBadge,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  Field,
  InlineError,
  LockIcon,
  Notice,
  PageHeader,
  Panel,
  PlusCircleIcon,
  RadioGroup,
  RowMenu,
  SelectField,
  SwatchPicker,
  SkeletonList,
  showToast,
  type DataTableColumn,
} from "../components/index.ts";
import { useClassRules, useCreateGroup, useMergeGroups, useSaveClassRules, useUpdateGroup } from "@teamna/web-client/hooks/use-groups.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { useActiveBusiness, useOwnerGroups } from "@teamna/web-client/hooks/use-owner.ts";
import { useShape } from "@teamna/web-client/hooks/use-shape.ts";
import { useTeam, useTimetable } from "@teamna/web-client/hooks/use-setup.ts";
import { GROUP_PALETTE, LEVEL_COLOR_CHOICES, groupColor } from "../lib/group-color.ts";
import { useStaffPhotos } from "./staff-photos.ts";
import { andList } from "@teamna/shared";
import { duplicateSets, type DuplicateSet } from "./level-merge.ts";
import { ClassRulesSection } from "./class-rules.tsx";

/**
 * Groups and sessions (web-app-v2.md, slice C): every active group of the business in one table.
 *
 * The table reads the timetable (`GET /business/.../timetable`), because that one call already
 * carries what the columns need: the default staff member's name, the slots per week and the
 * participants placed. Add and edit open a panel; delete sits in the row's More menu, so nobody
 * deletes a group by mistake, and it asks first. A row opens the group's own
 * page with its weekly slots.
 *
 * The setup step at `/owner/setup/sessions` stays for the first-day flow (upload a timetable, or
 * start from the sport template). This page is for the business that is already running.
 */
export function OwnerGroupsScreen() {
  const { business, isLoading, error } = useActiveBusiness();
  const { label } = useLabels();
  const businessId = business?.businessId ?? null;

  if (businessId) {
    return (
      <div>
        <GroupsBody businessId={businessId} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title={label("group", { plural: true, capital: true })} />
      {isLoading ? (
        <SkeletonList />
      ) : error ? (
        <ErrorState error={error} />
      ) : (
        <EmptyState
          icon={<LockIcon size={30} />}
          title="No business to manage"
          message="This account does not run a business yet. Create one from the start page."
        />
      )}
    </div>
  );
}

/** The columns of the groups table. Outside the component, so no cell is a new component on each render. */
function groupColumns({
  label,
  photoOf,
  onEdit,
  onDelete,
}: {
  label: ReturnType<typeof useLabels>["label"];
  /** A coach's photo, for the face next to the name. */
  photoOf: (userId: string) => string | null;
  /** Missing: the list is read only (the front desk without "Change classes"), with no Edit or More. */
  onEdit?: ((row: TimetableGroup) => void) | undefined;
  /** Sets the group archived: it leaves every list. The owner reads "Delete". */
  onDelete: (row: TimetableGroup) => void;
}): DataTableColumn<TimetableGroup>[] {
  const columns: DataTableColumn<TimetableGroup>[] = [
    {
      id: "name",
      header: "Name",
      sortValue: (row) => row.name,
      cell: (row) => (
        <span className="flex items-center gap-2.5">
          <span
            aria-hidden="true"
            className="size-3 shrink-0 rounded-full"
            style={{ backgroundColor: groupColor(row.color, row.colorIndex) }}
          />
          <span className="font-semibold text-ink">{row.name}</span>
          <ClassLevelBadge level={row.level} />
        </span>
      ),
    },
    {
      id: "kind",
      header: "Kind",
      hideOnPhone: true,
      cell: (row) =>
        row.kind === "private" ? (
          <Badge tone="quiet">{isDuet(row.kind, row.capacity) ? "Duet" : "Private"}</Badge>
        ) : row.openToAll ? (
          <Badge tone="quiet">Open to all</Badge>
        ) : (
          <span>{label("group", { capital: true })}</span>
        ),
    },
    {
      id: "staff",
      header: label("staff", { plural: true, capital: true }),
      sortValue: (row) => row.staffNames.join(", "),
      hideOnPhone: true,
      cell: (row) =>
        row.staffUserIds.length ? (
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {row.staffUserIds.map((userId, index) => (
              <AvatarName key={userId} name={row.staffNames[index] ?? ""} photoUrl={photoOf(userId)} />
            ))}
          </span>
        ) : (
          <span className="text-ink-3">None</span>
        ),
    },
    {
      id: "capacity",
      header: "Places",
      align: "end",
      sortValue: (row) => row.capacity,
      // Null is no limit.
      cell: (row) =>
        row.capacity === null ? <span className="text-ink-2">No limit</span> : <span className="tabular-nums">{row.capacity}</span>,
    },
    {
      id: "slots",
      header: "Sessions a week",
      align: "end",
      sortValue: (row) => row.slots.length,
      cell: (row) => <span className="tabular-nums">{row.slots.length}</span>,
    },
    {
      id: "placed",
      header: `${label("participant", { plural: true, capital: true })} placed`,
      align: "end",
      sortValue: (row) => row.participantCount,
      cell: (row) => <span className="tabular-nums">{row.participantCount}</span>,
    },
  ];
  if (!onEdit) return columns;
  return [
    ...columns,
    {
      id: "actions",
      header: "",
      align: "end",
      width: "120px",
      cell: (row) => (
        <span className="inline-flex items-center gap-1">
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onEdit(row);
            }}
            onKeyDown={(event) => event.stopPropagation()}
            aria-label={`Edit ${row.name}`}
            className="rounded-lg px-2.5 py-1.5 text-[13px] font-semibold text-accent-strong hover:bg-surface"
          >
            Edit
          </button>
          <RowMenu
            label={`More for ${row.name}`}
            items={[{ label: `Delete ${label("group")}`, tone: "danger", onSelect: () => onDelete(row) }]}
          />
        </span>
      ),
    },
  ];
}

export type FormState = { mode: "add" } | { mode: "edit"; group: TimetableGroup };

function GroupsBody({ businessId }: { businessId: string }) {
  const { label } = useLabels();
  const navigate = useNavigate();
  const timetable = useTimetable(businessId);
  const groups = useOwnerGroups(businessId);
  const team = useTeam(businessId);
  const update = useUpdateGroup(businessId);
  const [form, setForm] = useState<FormState | null>(null);
  const [deleting, setDeleting] = useState<TimetableGroup | null>(null);
  const [merging, setMerging] = useState<DuplicateSet | null>(null);

  const Groups = label("group", { plural: true, capital: true });
  const group = label("group");
  const rows = timetable.data?.groups ?? [];
  const staffOptions = (team.data ?? []).filter((member) => member.userId !== null);

  const photoOf = useStaffPhotos(businessId);
  // The front desk reads the classes, and changes them only with "Change classes" on (Settings, Front desk).
  const { may } = useActiveBusiness();
  const edits = may("editClass");
  const columns = groupColumns({ label, photoOf, onEdit: edits ? (row) => setForm({ mode: "edit", group: row }) : undefined, onDelete: setDeleting });

  return (
    <>
      <PageHeader
        title={Groups}
        description={
          may("addClass") ? `Each ${group} has a weekly schedule. Open a ${group} to change its days and times.` : `Each ${group} has a weekly schedule. Open a ${group} to see its days and times.`
        }
        actions={
          may("addClass") ? (
            <Button onClick={() => setForm({ mode: "add" })}>
              <span className="inline-flex items-center gap-2">
                <PlusCircleIcon size={17} />
                Add a {group}
              </span>
            </Button>
          ) : null
        }
      />

      {(may("mergeClasses") ? duplicateSets(rows) : []).map((set) => (
        <div key={set.keep.id} className="mb-4">
          <Notice
            tone="warning"
            title={`${set.others.length + 1} ${label("group", { plural: true })} have the same name: ${set.keep.name.trim()}.`}
            action={
              <Button variant="secondary" size="sm" className="self-start" onClick={() => setMerging(set)}>
                See the merge
              </Button>
            }
          >
            Merge them into one {group}. Each {label("staff")} stays on the {group}, and each weekly time keeps its{" "}
            {label("staff")}.
          </Notice>
        </div>
      ))}

      {timetable.error && !timetable.data ? (
        <ErrorState error={timetable.error} onRetry={() => void timetable.refetch()} />
      ) : (
        <DataTable
          caption={Groups}
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          rowLabel={(row) => row.name}
          onRowClick={(row) => void navigate({ to: "/owner/groups/$groupId", params: { groupId: row.id } })}
          isLoading={timetable.isLoading}
          empty={{
            title: `No ${label("group", { plural: true })} yet`,
            message: may("addClass") ? `Add a ${group}, or set up the whole schedule from the setup step.` : `The owner adds the ${label("group", { plural: true })}.`,
            ...(may("addClass") ? { action: <Button onClick={() => setForm({ mode: "add" })}>Add a {group}</Button> } : {}),
          }}
          countLabel={(count) => `${count} ${count === 1 ? group : label("group", { plural: true })}`}
        />
      )}

      {form ? (
        <GroupPanel
          key={form.mode === "edit" ? form.group.id : "add"}
          businessId={businessId}
          form={form}
          record={form.mode === "edit" ? (groups.data ?? []).find((row) => row.id === form.group.id) : undefined}
          count={rows.length}
          staffOptions={staffOptions}
          onClose={() => setForm(null)}
        />
      ) : null}

      <MergeDialog businessId={businessId} set={merging} onClose={() => setMerging(null)} />

      <ConfirmDialog
        open={deleting !== null}
        title={`Delete ${deleting?.name ?? ""}?`}
        confirmLabel={`Delete ${group}`}
        tone="danger"
        loading={update.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (!deleting) return;
          const name = deleting.name;
          update.mutate(
            { groupId: deleting.id, patch: { archived: true } },
            {
              onSuccess: () => {
                setDeleting(null);
                showToast(`${name} is deleted.`);
              },
              onError: () => {
                setDeleting(null);
                showToast(`We could not delete ${name}. Try again.`, { tone: "info" });
              },
            },
          );
        }}
      >
        <p>
          The {group} goes off your list. No new sessions are made for it. Sessions that are already on the schedule stay, with their
          bookings. Cancel them from the schedule if you need to.
        </p>
      </ConfirmDialog>
    </>
  );
}

/**
 * Merge levels with the same name into one (prototype `teamna/prototypes/level-coaches.html`). The
 * dialog says what stays and what moves; the server does it in one step.
 */
function MergeDialog({ businessId, set, onClose }: { businessId: string; set: DuplicateSet | null; onClose: () => void }) {
  const { label } = useLabels();
  const merge = useMergeGroups(businessId);
  const group = label("group");
  const name = set?.keep.name.trim() ?? "";
  return (
    <ConfirmDialog
      open={set !== null}
      title={`Merge into one ${name}?`}
      confirmLabel={`Merge ${label("group", { plural: true })}`}
      cancelLabel="Not now"
      loading={merge.isPending}
      onCancel={onClose}
      onConfirm={() => {
        if (!set) return;
        merge.mutate(
          { keepGroupId: set.keep.id, groupIds: set.others.map((row) => row.id) },
          {
            onSuccess: () => {
              onClose();
              showToast(`${name} is now one ${group}.`, { tone: "success" });
            },
            onError: () => showToast(`We could not merge ${name}. Try again.`, { tone: "info" }),
          },
        );
      }}
    >
      {set ? (
        <ul className="m-0 flex list-disc flex-col gap-1.5 pl-5">
          <li>
            One {group} stays: {name}. The other {set.others.length === 1 ? "one is" : `${set.others.length} are`} deleted.
          </li>
          <li>
            {label("staff", { plural: true, capital: true })}: {set.staffNames.length ? andList(set.staffNames) : "none yet"}.
          </li>
          <li>
            {set.weeklyTimes} weekly {set.weeklyTimes === 1 ? "time" : "times"}. Each one keeps its {label("staff")}.
          </li>
          <li>
            {set.participants} {label("participant", { plural: set.participants !== 1 })} move to this {group}.
          </li>
          <li>Bookings and sessions left do not change. No message goes out.</li>
        </ul>
      ) : null}
    </ConfirmDialog>
  );
}

/** The usual session lengths. The owner taps one; "Other" opens a minutes field for the rest. */
const LENGTH_CHOICES = [
  { minutes: 30, label: "30 min" },
  { minutes: 45, label: "45 min" },
  { minutes: 60, label: "1 hour" },
  { minutes: 90, label: "1 hour 30 min" },
];
const OTHER_LENGTH = "other";

/** The kind on the panel: a group, a private (one place) or a duet (a private with two places). */
type ClassType = "group" | "private" | "duet";

/** The "No level" choice of the level list. */
const NO_LEVEL = "none";

function toLevel(value: string): ClassLevel | null {
  return (classLevels as readonly string[]).includes(value) ? (value as ClassLevel) : null;
}

/** Add or edit one group: name, kind, open to all, minutes, places, coaches, and (edit) its booking rules. */
export function GroupPanel({
  businessId,
  form,
  record,
  count,
  staffOptions,
  onClose,
}: {
  businessId: string;
  form: FormState;
  record: Group | undefined;
  count: number;
  staffOptions: TeamMember[];
  onClose: () => void;
}) {
  const { label } = useLabels();
  const create = useCreateGroup(businessId);
  const update = useUpdateGroup(businessId);
  const editing = form.mode === "edit" ? form.group : null;
  // The group's own booking rules (B3). A new group starts on the business rules; set them after.
  const savedRules = useClassRules(businessId, editing?.id ?? null);
  const saveRules = useSaveClassRules(businessId);
  const [rules, setRules] = useState<ClassRules | null>(null);
  const shownRules = rules ?? savedRules.data?.rules ?? { maxBookAheadDays: null, cancelCutoffHours: null };
  const rulesChanged =
    rules !== null &&
    savedRules.data !== undefined &&
    (rules.maxBookAheadDays !== savedRules.data.rules.maxBookAheadDays || rules.cancelCutoffHours !== savedRules.data.rules.cancelCutoffHours);

  const { fixedGroups } = useShape(businessId);
  const [name, setName] = useState(editing?.name ?? "");
  // A duet is a private class for two (Studio S2): the kind stays private, with two places.
  const [kind, setKind] = useState<ClassType>(
    editing ? (editing.kind === "group" ? "group" : isDuet(editing.kind, editing.capacity) ? "duet" : "private") : "group",
  );
  const [level, setLevel] = useState<ClassLevel | null>(editing?.level ?? null);
  // Where each participant stays in one group, the level badge shows only when the owner set one.
  const showLevel = !fixedGroups || (editing?.level ?? null) !== null;
  const [minutes, setMinutes] = useState(String(editing?.durationMin ?? record?.durationMin ?? 45));
  // A length that is not one of the choices (an old level of 50 minutes) opens on "Other".
  const [otherLength, setOtherLength] = useState(
    () => !LENGTH_CHOICES.some((choice) => String(choice.minutes) === minutes),
  );
  // Focus the minutes field only after a tap on "Other", not when an edit opens on it.
  const minutesInput = useRef<HTMLInputElement>(null);
  // Places are open by default. A business sets a limit only when it wants one (null is no limit).
  const [limit, setLimit] = useState<"none" | "set">(editing && editing.capacity !== null ? "set" : "none");
  const [places, setPlaces] = useState(String(editing?.capacity ?? 8));
  const [staff, setStaff] = useState<string[]>(editing?.staffUserIds ?? []);
  // Open to all: every level books it with a package of its own (a training session, for example).
  const [openToAll, setOpenToAll] = useState(editing?.openToAll ?? false);
  // The level's colour on the calendar and the member home. A new level starts on the next palette colour.
  const savedColor = editing ? groupColor(editing.color, editing.colorIndex) : GROUP_PALETTE[count % GROUP_PALETTE.length]!;
  // A saved "#d4af37" is the Gold choice: use the choice's own spelling, so its swatch shows as picked.
  const startColor =
    LEVEL_COLOR_CHOICES.find((choice) => choice.value.toLowerCase() === savedColor.toLowerCase())?.value ?? savedColor;
  const [color, setColor] = useState<string>(startColor);
  // A colour from before the choices (or from the palette by index) stays a choice, so the picker shows it.
  const colorOptions = [
    ...(LEVEL_COLOR_CHOICES.some((choice) => choice.value === startColor)
      ? []
      : [{ value: startColor, label: "Current colour", color: startColor }]),
    ...LEVEL_COLOR_CHOICES.map((choice) => ({ value: choice.value as string, label: choice.label, color: choice.value })),
  ];
  const [problem, setProblem] = useState<string | null>(null);

  const group = label("group");
  const pending = create.isPending || update.isPending || saveRules.isPending;

  function submit() {
    const durationMin = Number(minutes);
    const defaultCapacity = kind === "private" ? 1 : kind === "duet" ? 2 : limit === "none" ? null : Number(places);
    if (!name.trim()) return setProblem(`Give the ${group} a name.`);
    if (!Number.isInteger(durationMin) || durationMin < 5 || durationMin > 600) {
      return setProblem("Minutes must be a whole number from 5 to 600.");
    }
    if (defaultCapacity !== null && (!Number.isInteger(defaultCapacity) || defaultCapacity < 1 || defaultCapacity > 500)) {
      return setProblem("Places must be a whole number from 1 to 500.");
    }
    setProblem(null);
    const fields = {
      name: name.trim(),
      kind: kind === "group" ? ("group" as const) : ("private" as const),
      durationMin,
      defaultCapacity,
      staffUserIds: staff,
      openToAll: kind === "group" && openToAll,
      ...(showLevel ? { level } : {}),
    };
    if (editing) {
      update.mutate(
        // The colour only when it changed: a level coloured by its index keeps following the palette.
        { groupId: editing.id, patch: color === startColor ? fields : { ...fields, color } },
        {
          onSuccess: () => {
            const done = () => {
              showToast(`${fields.name} is saved.`);
              onClose();
            };
            if (rulesChanged && rules) saveRules.mutate({ groupId: editing.id, rules }, { onSuccess: done });
            else done();
          },
        },
      );
    } else {
      create.mutate(
        { ...fields, bookingModel: "open", color, sortOrder: count },
        {
          onSuccess: () => {
            showToast(`${fields.name} is added. Open it to give it weekly sessions.`);
            onClose();
          },
        },
      );
    }
  }

  return (
    <Panel
      open
      onClose={onClose}
      title={editing ? `Edit ${editing.name}` : `Add a ${group}`}
      subtitle={editing ? "Changes apply to new sessions. Weekly times are on the next page." : undefined}
      actions={
        <>
          <Button loading={pending} onClick={submit}>
            {editing ? "Save" : `Add ${group}`}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {problem ? <Notice tone="warning" title={problem} /> : null}
        {create.error ? <InlineError error={create.error} /> : null}
        {update.error ? <InlineError error={update.error} /> : null}
        {saveRules.error ? <InlineError error={saveRules.error} /> : null}
        <Field label="Name" value={name} maxLength={120} onChange={(event) => setName(event.target.value)} />
        <div className="flex flex-col gap-2">
          <p className="eyebrow m-0 text-ink-2" aria-hidden="true">
            Colour
          </p>
          <SwatchPicker label={`Colour of the ${group}`} options={colorOptions} value={color} onChange={setColor} />
          <p className="m-0 text-[12.5px] text-ink-3">
            {colorOptions.find((option) => option.value === color)?.label}. The {group} has this colour on the calendar and in the app.
          </p>
        </div>
        <SelectField
          label="Kind"
          value={kind}
          onChange={(event) => setKind(event.target.value === "private" ? "private" : event.target.value === "duet" ? "duet" : "group")}
        >
          <option value="group">{label("group", { capital: true })}, several {label("participant", { plural: true })}</option>
          <option value="private">Private, one {label("participant")}</option>
          <option value="duet">Duet, two {label("participant", { plural: true })}</option>
        </SelectField>
        {showLevel ? (
          <SelectField
            label="Level"
            hint={`${label("member", { plural: true, capital: true })} see the level on the session card when they book.`}
            value={level ?? NO_LEVEL}
            onChange={(event) => setLevel(toLevel(event.target.value))}
          >
            <option value={NO_LEVEL}>No level</option>
            {classLevels.map((value) => (
              <option key={value} value={value}>
                {classLevelWords[value]}
              </option>
            ))}
          </SelectField>
        ) : null}
        {kind === "group" ? (
          <Checkbox
            label={`Open to all ${label("group", { plural: true })}`}
            hint={`Any ${label("participant")} can book it with a package for this ${group}. Nobody is placed in it. Use it for a training session for all ${label("group", { plural: true })}.`}
            checked={openToAll}
            onChange={setOpenToAll}
          />
        ) : null}
        <div className="flex flex-col gap-2">
          <p className="eyebrow m-0 text-ink-2" aria-hidden="true">
            Session length
          </p>
          <ChipRow
            wrap
            label="Session length"
            options={[
              ...LENGTH_CHOICES.map((choice) => ({ id: String(choice.minutes), label: choice.label })),
              { id: OTHER_LENGTH, label: "Other" },
            ]}
            selectedId={otherLength ? OTHER_LENGTH : minutes}
            onSelect={(id) => {
              if (id === OTHER_LENGTH) {
                // Show the field first, then focus it in the same tap.
                flushSync(() => setOtherLength(true));
                return minutesInput.current?.focus();
              }
              setOtherLength(false);
              setMinutes(id);
            }}
          />
          {otherLength ? (
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Minutes"
                type="number"
                inputMode="numeric"
                min={5}
                max={600}
                ref={minutesInput}
                value={minutes}
                onChange={(event) => setMinutes(event.target.value)}
              />
            </div>
          ) : null}
        </div>
        {kind === "private" || kind === "duet" ? (
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Places"
              value={kind === "duet" ? "2" : "1"}
              disabled
              hint={
                kind === "duet"
                  ? `A duet has two places. Each person pays one private session.`
                  : `A private ${group} has one place.`
              }
            />
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <RadioGroup<"none" | "set">
              legend="Places"
              value={limit}
              onChange={setLimit}
              options={[
                { value: "none", label: "No limit", hint: "Anyone can book." },
                { value: "set", label: "Set a limit", hint: "Bookings stop when the session is full." },
              ]}
            />
            {limit === "set" ? (
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="Most people in one session"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={500}
                  value={places}
                  onChange={(event) => setPlaces(event.target.value)}
                />
              </div>
            ) : null}
          </div>
        )}
        <fieldset className="m-0 flex min-w-0 flex-col gap-2 border-0 p-0">
          <legend className="mb-1 text-[13px] font-semibold text-ink">{label("staff", { plural: true, capital: true })}</legend>
          {staffOptions.length === 0 ? (
            <p className="text-[13px] text-ink-3">Add people to the team first.</p>
          ) : (
            staffOptions.map((member) => (
              <Checkbox
                key={member.userId!}
                label={member.name}
                face={<Avatar name={member.name} photoUrl={member.photoUrl ?? null} size={22} />}
                checked={staff.includes(member.userId!)}
                onChange={(on) =>
                  setStaff((current) => (on ? [...current, member.userId!] : current.filter((id) => id !== member.userId)))
                }
              />
            ))
          )}
          <p className="text-[12.5px] text-ink-3">
            {staff.length > 1
              ? `Each weekly time has one of these ${label("staff", { plural: true })}. Set it on the schedule.`
              : `Pick one or more. A ${group} can have more than one ${label("staff")}.`}
          </p>
        </fieldset>
        {editing ? (
          <ClassRulesSection
            business={savedRules.data?.business ?? null}
            value={shownRules}
            onChange={setRules}
            loading={savedRules.isLoading}
            error={savedRules.error}
          />
        ) : null}
      </div>
    </Panel>
  );
}
